import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './NotificationsPanel.css';

const SEVERITY_OPTIONS = ['Info', 'Update', 'Warning', 'Critical'];

function emptyForm() {
  return {
    Id: '',
    Title: '',
    Body: '',
    PublishedUtc: new Date().toISOString().slice(0, 16),
    Url: '',
    Severity: 'Info',
    MinVersion: '',
    MaxVersion: '',
    IsPublished: true,
  };
}

function toDatetimeLocal(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const tzOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
}

function buildPayload(formData) {
  return {
    Id: formData.Id?.trim() || undefined,
    Title: formData.Title.trim(),
    Body: formData.Body.trim(),
    PublishedUtc: formData.PublishedUtc ? new Date(formData.PublishedUtc).toISOString() : undefined,
    Url: formData.Url.trim() || null,
    Severity: formData.Severity,
    MinVersion: formData.MinVersion.trim() || null,
    MaxVersion: formData.MaxVersion.trim() || null,
    IsPublished: formData.IsPublished,
  };
}

function NotificationsPanel({ authToken, onAuthExpired, showAlert, showConfirm }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm());

  const editingNotification = useMemo(
    () => notifications.find((n) => n.Id === editingId) || null,
    [editingId, notifications]
  );

  const fetchNotifications = useCallback(async () => {
    if (!authToken) return;
    try {
      setLoading(true);
      const response = await fetch('/api/notifications', {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.status === 401 || response.status === 403) {
        showAlert('error', 'Session expired. Please login again.', onAuthExpired);
        return;
      }
      const data = await response.json();
      setNotifications(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      showAlert('error', 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [authToken, onAuthExpired, showAlert]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const startCreate = () => {
    setEditingId(null);
    setFormData(emptyForm());
    setShowForm(true);
  };

  const startEdit = (notification) => {
    setEditingId(notification.Id);
    setFormData({
      Id: notification.Id || '',
      Title: notification.Title || '',
      Body: notification.Body || '',
      PublishedUtc: toDatetimeLocal(notification.PublishedUtc),
      Url: notification.Url || '',
      Severity: notification.Severity || 'Info',
      MinVersion: notification.MinVersion || '',
      MaxVersion: notification.MaxVersion || '',
      IsPublished: Boolean(notification.IsPublished),
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData(emptyForm());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.Title.trim() || !formData.Body.trim()) {
      showAlert('error', 'Title and body are required.');
      return;
    }
    try {
      setSaving(true);
      const isEditing = Boolean(editingNotification);
      const endpoint = isEditing
        ? `/api/notifications/${encodeURIComponent(editingNotification.Id)}`
        : '/api/notifications';
      const method = isEditing ? 'PUT' : 'POST';
      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(buildPayload(formData)),
      });
      if (response.status === 401 || response.status === 403) {
        showAlert('error', 'Session expired. Please login again.', onAuthExpired);
        return;
      }
      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        showAlert('error', errorPayload.error || 'Failed to save notification');
        return;
      }
      showAlert('success', isEditing ? 'Notification updated' : 'Notification created');
      closeForm();
      fetchNotifications();
    } catch (error) {
      console.error('Error saving notification:', error);
      showAlert('error', 'Failed to save notification');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (notification) => {
    showConfirm(`Delete notification "${notification.Title}"?`, async () => {
      try {
        const response = await fetch(`/api/notifications/${encodeURIComponent(notification.Id)}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (response.status === 401 || response.status === 403) {
          showAlert('error', 'Session expired. Please login again.', onAuthExpired);
          return;
        }
        if (!response.ok) {
          const errorPayload = await response.json().catch(() => ({}));
          showAlert('error', errorPayload.error || 'Failed to delete notification');
          return;
        }
        showAlert('success', 'Notification deleted');
        fetchNotifications();
      } catch (error) {
        console.error('Error deleting notification:', error);
        showAlert('error', 'Failed to delete notification');
      }
    });
  };

  const togglePublish = async (notification) => {
    try {
      const response = await fetch(`/api/notifications/${encodeURIComponent(notification.Id)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          Title: notification.Title,
          Body: notification.Body,
          PublishedUtc: notification.PublishedUtc,
          Url: notification.Url,
          Severity: notification.Severity,
          MinVersion: notification.MinVersion,
          MaxVersion: notification.MaxVersion,
          IsPublished: !notification.IsPublished,
        }),
      });
      if (response.status === 401 || response.status === 403) {
        showAlert('error', 'Session expired. Please login again.', onAuthExpired);
        return;
      }
      if (!response.ok) {
        showAlert('error', 'Failed to update publish state');
        return;
      }
      showAlert('success', notification.IsPublished ? 'Notification unpublished' : 'Notification published');
      fetchNotifications();
    } catch (error) {
      console.error('Error toggling publish state:', error);
      showAlert('error', 'Failed to update publish state');
    }
  };

  return (
    <section className="notifications-panel">
      <div className="notifications-header">
        <div>
          <h2>Notifications Feed</h2>
          <p>
            Post developer/admin updates that the TreeWorks ArcGIS Pro add-in reads from{' '}
            <a href="/news/feed.json" target="_blank" rel="noopener noreferrer">
              <code>/news/feed.json</code>
            </a>
            .
          </p>
        </div>
        <div className="notifications-actions">
          <button className="btn btn-primary" onClick={showForm ? closeForm : startCreate}>
            {showForm ? 'Cancel' : '+ New Notification'}
          </button>
          <button className="btn btn-secondary" onClick={fetchNotifications}>Refresh</button>
        </div>
      </div>

      {showForm && (
        <div className="form-container notifications-form">
          <h3>{editingNotification ? 'Edit Notification' : 'Create Notification'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Feed Id (optional — auto-generated)</label>
                <input
                  type="text"
                  name="Id"
                  value={formData.Id}
                  onChange={handleInputChange}
                  placeholder="2026-05-04-feature-update"
                  disabled={Boolean(editingNotification)}
                  maxLength={100}
                />
              </div>
              <div className="form-group">
                <label>Severity</label>
                <select name="Severity" value={formData.Severity} onChange={handleInputChange}>
                  {SEVERITY_OPTIONS.map((severity) => (
                    <option key={severity} value={severity}>{severity}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label>Title *</label>
                <input type="text" name="Title" value={formData.Title} onChange={handleInputChange} maxLength={255} required />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label>Body * (plain text — line breaks supported)</label>
                <textarea name="Body" value={formData.Body} onChange={handleInputChange} rows={4} required />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Publish Date (UTC)</label>
                <input type="datetime-local" name="PublishedUtc" value={formData.PublishedUtc} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Read More URL (HTTPS)</label>
                <input type="url" name="Url" value={formData.Url} onChange={handleInputChange} placeholder="https://www.treeauth.com/release-notes/1.2" />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Minimum Version (optional)</label>
                <input type="text" name="MinVersion" value={formData.MinVersion} onChange={handleInputChange} placeholder="1.0.0" />
              </div>
              <div className="form-group">
                <label>Maximum Version (optional)</label>
                <input type="text" name="MaxVersion" value={formData.MaxVersion} onChange={handleInputChange} placeholder="2.0.0" />
              </div>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input type="checkbox" name="IsPublished" checked={formData.IsPublished} onChange={handleInputChange} />
                Publish immediately (visible in public feed)
              </label>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : editingNotification ? 'Save Changes' : 'Create Notification'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={closeForm} disabled={saving}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading notifications...</div>
      ) : notifications.length === 0 ? (
        <div className="empty-state">
          <p>No notifications yet. Create one to publish updates into ArcGIS Pro.</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="licenses-table notifications-table">
            <thead>
              <tr>
                <th>Id</th>
                <th>Title</th>
                <th>Published</th>
                <th>Severity</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {notifications.map((notification) => (
                <tr key={notification.Id}>
                  <td className="notification-id" data-label="Id">{notification.Id}</td>
                  <td data-label="Title">{notification.Title}</td>
                  <td data-label="Published">
                    {notification.PublishedUtc ? new Date(notification.PublishedUtc).toLocaleString() : 'N/A'}
                  </td>
                  <td data-label="Severity">
                    <span className={`severity-badge sev-${(notification.Severity || 'Info').toLowerCase()}`}>
                      {notification.Severity || 'Info'}
                    </span>
                  </td>
                  <td data-label="Status">
                    <span className={`status-badge ${notification.IsPublished ? 'status-active' : 'status-warning'}`}>
                      {notification.IsPublished ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="actions" data-label="Actions">
                    <button className="action-btn edit-btn" onClick={() => startEdit(notification)}>Edit</button>
                    <button
                      className={`action-btn toggle-btn ${notification.IsPublished ? 'active' : 'inactive'}`}
                      onClick={() => togglePublish(notification)}
                    >
                      {notification.IsPublished ? 'Unpublish' : 'Publish'}
                    </button>
                    <button className="action-btn delete-btn" onClick={() => handleDelete(notification)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default NotificationsPanel;
